import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import SuiInput from "components/SuiInput";
import CustomSelect from "components/CustomSelect";
import ErrorAlert from "components/ErrorAlert";
import Pagination from "utils/Pagination";
import goalSelectStyles from "layouts/goals/components/goalSelectStyles";
import useEmployees from "./useEmployees";
import EmployeesTable from "./components/EmployeesTable";
import EmployeesTabs from "./components/EmployeesTabs";
import EmployeeFormModal from "./components/EmployeeFormModal";
import ArchiveEmployeeModal from "./components/ArchiveEmployeeModal";
import EmployeeAssetsModal from "./components/EmployeeAssetsModal";
import { EMPLOYMENT_STATUSES } from "./employees.constants";
import usePermissions from "utils/usePermissions";
import { PERMISSIONS } from "utils/permissions";
import useAssets from "layouts/assets/useAssets";
import { groupAssetsByUser } from "layouts/assets/assets.constants";
import AssignAssetModal from "layouts/assets/components/AssignAssetModal";
import ReturnAssetModal from "layouts/assets/components/ReturnAssetModal";
import DisposeAssetModal from "layouts/assets/components/DisposeAssetModal";

const PAGE_SIZE = 10;

const STATUS_FILTER_OPTIONS = [{ value: "", label: "All statuses" }, ...EMPLOYMENT_STATUSES];

function Employees() {
  const {
    auth: { admin, team, channel },
  } = useSelector((state) => state);

  const {
    employees,
    isLoading,
    errorMessage,
    successMessage,
    fetchAll,
    updateEmployee,
    archiveEmployee,
    clearError,
    clearSuccess,
  } = useEmployees({ adminId: admin?.id, teamId: team?.id, channelId: channel?.id });

  // Asset management is its own permission; without it the page works as before.
  const canManageAssets = usePermissions().can(PERMISSIONS.ASSETS_MANAGE);
  const assetsState = useAssets(admin?.id);
  const { assets, assignees } = assetsState;
  const assetGroups = useMemo(() => groupAssetsByUser(assets), [assets]);
  const [assetsFor, setAssetsFor] = useState(null);
  const [assigning, setAssigning] = useState(false);
  const [returning, setReturning] = useState(null);
  const [disposing, setDisposing] = useState(null);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [tab, setTab] = useState("active");
  const [editing, setEditing] = useState(null);
  const [toArchive, setToArchive] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  // Follows the workspace and channel picked in the navbar.
  useEffect(() => {
    fetchAll();
  }, [admin?.id, team?.id, channel?.id]);

  useEffect(() => {
    if (canManageAssets) assetsState.fetchAll();
  }, [admin?.id, canManageAssets]);

  // Archived = no active channel membership left (is_tracked is false).
  const tabs = useMemo(
    () => [
      { value: "active", label: "Active", count: employees.filter((e) => e.is_tracked).length },
      {
        value: "archived",
        label: "Archived",
        count: employees.filter((e) => !e.is_tracked).length,
      },
    ],
    [employees]
  );

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    return employees.filter((employee) => {
      if (employee.is_tracked !== (tab === "active")) return false;
      if (statusFilter && employee.employment_status !== statusFilter) return false;
      if (!term) return true;
      return [
        employee.name,
        employee.display_name,
        employee.user_name,
        employee.employee_id,
        employee.job_title,
      ].some((value) => value && value.toLowerCase().includes(term));
    });
  }, [employees, search, statusFilter, tab]);

  // A new search, filter or tab starts again from the first page.
  useEffect(() => {
    setCurrentPage(1);
  }, [search, statusFilter, tab]);

  const pageRows = visible.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const handleSave = async (body) => {
    setIsSubmitting(true);
    const ok = await updateEmployee(editing.id, body);
    setIsSubmitting(false);
    if (ok) setEditing(null);
  };

  const handleConfirmArchive = async () => {
    setIsSubmitting(true);
    const ok = await archiveEmployee(toArchive.id);
    setIsSubmitting(false);
    if (ok) setToArchive(null);
  };

  // Runs an asset request with the button spinner and closes its dialog on success.
  const submitAsset =
    (action, close) =>
    async (...args) => {
      setIsSubmitting(true);
      const ok = await action(...args);
      setIsSubmitting(false);
      if (ok) close();
    };
  const handleAssign = submitAsset(assetsState.assignAsset, () => setAssigning(false));
  const handleReturn = submitAsset(assetsState.returnAsset, () => setReturning(null));
  const handleDispose = submitAsset(assetsState.disposeAsset, () => setDisposing(null));

  const assetsError = assetsState.errorMessage;
  const assetsSuccess = assetsState.successMessage;
  const spareAssets = assets.filter((asset) => asset.state === "in_stock");

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SuiBox py={3}>
        {errorMessage && (
          <ErrorAlert color="error" message={errorMessage} clearError={clearError} />
        )}
        {successMessage && (
          <ErrorAlert color="success" message={successMessage} clearError={clearSuccess} />
        )}
        {assetsError && (
          <ErrorAlert color="error" message={assetsError} clearError={assetsState.clearError} />
        )}
        {assetsSuccess && (
          <ErrorAlert
            color="success"
            message={assetsSuccess}
            clearError={assetsState.clearSuccess}
          />
        )}
        <SuiBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          gap={2}
          mb={3}
        >
          <SuiTypography variant="h5" fontWeight="medium">
            Employees
          </SuiTypography>
          <SuiBox display="flex" gap={2} alignItems="center">
            <SuiBox minWidth="240px">
              <SuiInput
                placeholder="Search name, ID, title..."
                icon={{ component: "search", direction: "left" }}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </SuiBox>
            <SuiBox minWidth="170px">
              <CustomSelect
                options={STATUS_FILTER_OPTIONS}
                value={STATUS_FILTER_OPTIONS.find((option) => option.value === statusFilter)}
                onChange={(option) => setStatusFilter(option.value)}
                customStyles={goalSelectStyles}
                menuPortalTarget={document.body}
              />
            </SuiBox>
          </SuiBox>
        </SuiBox>
        <EmployeesTabs tabs={tabs} value={tab} onChange={setTab} />
        <EmployeesTable
          employees={pageRows}
          onEdit={setEditing}
          onArchive={setToArchive}
          isLoading={isLoading}
          assetGroups={assetGroups}
          onAssets={canManageAssets ? setAssetsFor : undefined}
        />
        {!isLoading && (
          <SuiBox mt={3}>
            <Pagination
              currentPage={currentPage}
              totalCount={visible.length}
              pageSize={PAGE_SIZE}
              onPageChange={setCurrentPage}
            />
          </SuiBox>
        )}
        <ArchiveEmployeeModal
          open={!!toArchive}
          onClose={() => setToArchive(null)}
          onConfirm={handleConfirmArchive}
          employeeName={toArchive?.name}
          isSubmitting={isSubmitting}
        />
        <EmployeeFormModal
          open={!!editing}
          onClose={() => setEditing(null)}
          onSave={handleSave}
          isSubmitting={isSubmitting}
          employee={editing}
        />
        {canManageAssets && (
          <>
            <EmployeeAssetsModal
              open={!!assetsFor}
              onClose={() => setAssetsFor(null)}
              employee={assetsFor}
              groups={assetsFor && assetGroups[assetsFor.user_slack_id]}
              onAssign={() => setAssigning(true)}
              onReturn={setReturning}
              onDispose={setDisposing}
            />
            <AssignAssetModal
              open={assigning}
              onClose={() => setAssigning(false)}
              onAssign={handleAssign}
              isSubmitting={isSubmitting}
              asset={null}
              employee={assetsFor}
              spareAssets={spareAssets}
              assignees={assignees}
            />
            <ReturnAssetModal
              open={!!returning}
              onClose={() => setReturning(null)}
              onReturn={handleReturn}
              isSubmitting={isSubmitting}
              asset={returning}
            />
            <DisposeAssetModal
              open={!!disposing}
              onClose={() => setDisposing(null)}
              onDispose={handleDispose}
              isSubmitting={isSubmitting}
              asset={disposing}
            />
          </>
        )}
      </SuiBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Employees;
